import { useMemo } from "react";

import { useTranslation } from "@impero-com/common/utils/i18n";
import {
    TST_BLESSED_ID_COLUMN_SELECTION,
    TST_COLUMN_SIZE,
} from "@impero-com/ui";
import {
    createColumnHelper,
    FilterFn,
    getCoreRowModel,
    getFilteredRowModel,
    getSortedRowModel,
    OnChangeFn,
    RowSelectionState,
    useReactTable,
} from "@tanstack/react-table";
import { Checkbox } from "antd";

import { EntityListLinkedResources, EntityOverview } from "api/generated/model";
import { formatTagsAndCategoriesString } from "org/tags/tagsTSTUtils";

import { buildSharedColumns, EntityTableRow } from "./utils";

interface UseAddEntitiesTableProps {
    linkedResources: EntityListLinkedResources;
    controlEntities: EntityOverview[];
    selectedRows: { [key: string]: boolean };
    onSelectedRowsChange: OnChangeFn<RowSelectionState>;
    globalFilter: string;
    setGlobalFilter: (value: string) => void;
}

export function useAddEntitiesTable({
    linkedResources,
    controlEntities,
    selectedRows,
    onSelectedRowsChange,
    globalFilter,
    setGlobalFilter,
}: UseAddEntitiesTableProps) {
    const { t } = useTranslation();

    const sharedColumns = useMemo(
        () => buildSharedColumns(t, linkedResources),
        [t, linkedResources]
    );
    const selectionColumn = useMemo(() => buildSelectionColumn(), []);

    const columns = useMemo(
        () => [...selectionColumn, ...sharedColumns],
        [sharedColumns, selectionColumn]
    );
    const tableData: EntityTableRow[] = useMemo(() => {
        return controlEntities.map((entity) => ({
            entityId: entity.id,
            name: entity.name,
            tagIds: entity.tagIds,
            controlAccessRights: entity.controlAccessRights,
        }));
    }, [controlEntities]);

    const globalFilterFn: FilterFn<EntityTableRow> = (
        row,
        _columnId,
        filterValue
    ) => {
        if (!filterValue) {
            return true;
        }
        const search: string = filterValue.toLowerCase();
        const { name, tagIds } = row.original;
        const tags = formatTagsAndCategoriesString(tagIds, {
            tags: linkedResources.tags,
            tagCategories: linkedResources.tagCategories,
        });
        return (
            name.toLowerCase().includes(search) ||
            tags.toLowerCase().includes(search)
        );
    };

    const table = useReactTable({
        data: tableData,
        columns,
        enableRowSelection: true,
        onRowSelectionChange: onSelectedRowsChange,
        state: {
            globalFilter,
            rowSelection: selectedRows,
        },
        onGlobalFilterChange: setGlobalFilter,
        globalFilterFn,
        getRowId: (row) => row.entityId.toString(),
        getCoreRowModel: getCoreRowModel<EntityTableRow>(),
        getSortedRowModel: getSortedRowModel<EntityTableRow>(),
        getFilteredRowModel: getFilteredRowModel<EntityTableRow>(),
    });
    return table;
}

const columnHelper = createColumnHelper<EntityTableRow>();

const buildSelectionColumn = () => [
    columnHelper.display({
        id: TST_BLESSED_ID_COLUMN_SELECTION,
        cell: (info) => {
            const onClick = () => {
                info.row.toggleSelected();
            };
            return (
                <Checkbox
                    checked={info.row.getIsSelected()}
                    onClick={onClick}
                />
            );
        },
        header: (info) => {
            return (
                <Checkbox
                    checked={info.table.getIsAllPageRowsSelected()}
                    indeterminate={info.table.getIsSomePageRowsSelected()}
                    onChange={info.table.getToggleAllRowsSelectedHandler()}
                />
            );
        },
        size: TST_COLUMN_SIZE.CHECKBOX,
        meta: { fixedWidth: true },
    }),
];
